import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutate as globalMutate } from 'swr';
import { Building2, Check, Layers, Loader2, Tag, X } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '@/lib/axios';
import { useUser } from '@/hooks/use-user';
import { useDiscardChangesConfirm } from '@/hooks/use-discard-changes-confirm';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import type { PublicBusiness } from '@/types/profile';
import type { BusinessAddress } from '@/types';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { ImageUpload } from '@/components/ui/image-upload';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { parseBusinessCategory } from '@/lib/business-category';

const basicInfoSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  businessCategory: z.string().trim().min(1, 'Category is required'),
  subCategory: z.string().trim().min(1, 'Sub category is required'),
  selfDefinedCategory: z.string().trim().max(255).optional().or(z.literal('')),
  logo: z.string().optional(),
  priority: z.enum(['PRIMARY', 'SECONDARY', 'OTHER']),
});

type BasicInfoFormValues = z.infer<typeof basicInfoSchema>;

export type BusinessBasicInfoSaved = {
  name: string;
  logo?: string;
  category: string;
  subCategory: string;
  selfDefinedCategory: string | null;
};

interface BusinessBasicInfoSectionProps {
  business: PublicBusiness;
  onCancel: () => void;
  onSaved?: (updated: BusinessBasicInfoSaved) => void;
  /** Render form fields without an outer card (e.g. inside profile header). */
  embedded?: boolean;
}

function resolveAddressJson(business: PublicBusiness): BusinessAddress {
  const addr =
    business.addressJson && typeof business.addressJson === 'object'
      ? (business.addressJson as BusinessAddress)
      : undefined;

  if (
    addr?.addressLine1 &&
    addr.state &&
    addr.country &&
    addr.pinCode &&
    addr.pinCode.length >= 4
  ) {
    return {
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2 || '',
      landmark: addr.landmark || '',
      taluka: addr.taluka || '',
      district: addr.district || '',
      state: addr.state,
      country: addr.country,
      pinCode: addr.pinCode,
      googleMapLink: addr.googleMapLink || '',
    };
  }

  return {
    addressLine1: addr?.addressLine1 || business.address || 'Not provided',
    addressLine2: addr?.addressLine2 || '',
    landmark: addr?.landmark || '',
    taluka: addr?.taluka || '',
    district: addr?.district || '',
    state: addr?.state || 'Not provided',
    country: addr?.country || 'Not provided',
    pinCode: addr?.pinCode && addr.pinCode.length >= 4 ? addr.pinCode : '000000',
    googleMapLink: addr?.googleMapLink || '',
  };
}

export function BusinessBasicInfoSection({
  business,
  onCancel,
  onSaved,
  embedded = false,
}: BusinessBasicInfoSectionProps) {
  const { user, mutate: mutateUser } = useUser();
  const [saving, setSaving] = useState(false);

  const resolveCategoryDefaults = (ownedBiz?: {
    businessCategory?: string;
    subCategory?: string;
  }) => {
    if (ownedBiz?.businessCategory?.trim() && ownedBiz?.subCategory?.trim()) {
      return {
        businessCategory: ownedBiz.businessCategory.trim(),
        subCategory: ownedBiz.subCategory.trim(),
      };
    }
    if (business.subCategory?.trim()) {
      return {
        businessCategory: (business.category || '').trim(),
        subCategory: business.subCategory.trim(),
      };
    }
    return parseBusinessCategory(business.category || ownedBiz?.businessCategory);
  };

  const owned = user?.businesses?.find((b) => b.id === business.id);
  const initialCategory = resolveCategoryDefaults(owned);

  const form = useForm<BasicInfoFormValues>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: business.name || '',
      businessCategory: initialCategory.businessCategory,
      subCategory: initialCategory.subCategory,
      selfDefinedCategory: business.selfDefinedCategory || '',
      logo: business.logo || '',
      priority: (owned?.priority as 'PRIMARY' | 'SECONDARY' | 'OTHER' | undefined) ?? 'OTHER',
    },
  });

  useEffect(() => {
    const ownedBiz = user?.businesses?.find((b) => b.id === business.id);
    const categoryDefaults = resolveCategoryDefaults(ownedBiz);
    form.reset({
      name: business.name || '',
      businessCategory: categoryDefaults.businessCategory,
      subCategory: categoryDefaults.subCategory,
      selfDefinedCategory: business.selfDefinedCategory || '',
      logo: business.logo || '',
      priority: (ownedBiz?.priority as 'PRIMARY' | 'SECONDARY' | 'OTHER' | undefined) ?? 'OTHER',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.id, user?.businesses, form]);

  const watchedBusinessCategory = form.watch('businessCategory');
  const watchedSubCategory = form.watch('subCategory');

  const handleSave = async () => {
    const valid = await form.trigger();
    if (!valid) {
      throw new Error('Validation failed');
    }

    const values = form.getValues();
    setSaving(true);
    try {
      await axiosInstance.patch('/api/user/business', {
        id: business.id,
        name: values.name.trim(),
        businessCategory: values.businessCategory.trim(),
        subCategory: values.subCategory.trim(),
        selfDefinedCategory: values.selfDefinedCategory?.trim() || '',
        logo: values.logo || '',
        priority: values.priority,
        phone: business.phone,
        email: business.email,
        addressJson: resolveAddressJson(business),
      });

      await mutateUser();
      await globalMutate(`/api/public/business/${business.id}`);

      onSaved?.({
        name: values.name.trim(),
        logo: values.logo || undefined,
        category: values.businessCategory.trim(),
        subCategory: values.subCategory.trim(),
        selfDefinedCategory: values.selfDefinedCategory?.trim() || null,
      });

      toast.success('Basic information updated');
      onCancel();
    } catch (error) {
      if ((error as Error)?.message !== 'Validation failed') {
        toast.error('Failed to update basic information');
      }
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const isDirty = form.formState.isDirty;
  const { requestDiscard, dialog: discardDialog } = useDiscardChangesConfirm({
    when: isDirty,
    onSave: handleSave,
  });
  const { dialog: navGuardDialog } = useUnsavedChangesGuard({ when: isDirty });

  const exitEdit = () => requestDiscard(onCancel);

  const formBody = (
    <div className={embedded ? 'space-y-5' : 'space-y-5'}>
      {discardDialog}
      {navGuardDialog}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-full">
            <Building2 className="h-4 w-4 text-primary" />
          </div>
          <h2 className="text-base md:text-lg font-semibold tracking-tight">Basic Information</h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={exitEdit}
            disabled={saving}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => void handleSave().catch(() => undefined)}
            disabled={saving}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
            aria-label="Save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Form {...form}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave().catch(() => undefined);
          }}
        >
          <FormField
            control={form.control}
            name="logo"
            render={({ field }) => (
              <FormItem className="flex justify-center">
                <FormControl>
                  <ImageUpload value={field.value} onChange={field.onChange} disabled={saving} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Business Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Acme Corporation" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="businessCategory"
            render={() => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  Category <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <BusinessCategoryPicker
                    businessCategory={watchedBusinessCategory}
                    subCategory={watchedSubCategory}
                    onSelect={(value) => {
                      if (value) {
                        form.setValue('businessCategory', value.businessCategory, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        form.setValue('subCategory', value.subCategory, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else {
                        form.setValue('businessCategory', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        form.setValue('subCategory', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }}
                    placeholder="Search and select a category"
                  />
                </FormControl>
                {(form.formState.errors.businessCategory || form.formState.errors.subCategory) && (
                  <p className="text-sm font-medium text-destructive">
                    {form.formState.errors.businessCategory?.message ||
                      form.formState.errors.subCategory?.message}
                  </p>
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="selfDefinedCategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  Self-Defined Category
                </FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Premium Wedding Photography" {...field} />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Optional. Shown on your profile and in the directory when set.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  Your role among your businesses
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value || 'OTHER'}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="PRIMARY">Primary business</SelectItem>
                    <SelectItem value="SECONDARY">Secondary business</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used when resolving ties (e.g. curated community directory).
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2 pt-2 sm:hidden">
            <Button type="button" variant="outline" onClick={exitEdit} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <Card className="profile-card border-border/70 shadow-sm overflow-hidden">
      <CardContent className="p-5 md:p-7">{formBody}</CardContent>
    </Card>
  );
}
